import { Directive, Input, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';

export interface CesCheckableMenuItem extends MenuItem {
  checkable?: boolean;
  checked?: boolean;
  closeOnClick?: boolean;
  exclusiveGroup?: string;
  alwaysSelected?: boolean;
}

@Directive({
  selector: '[cesCheckableMenuItem]',
  standalone: true,
  exportAs: 'cesCheckableMenuItem',
  host: {
    '(click)': 'onClick($event)',
  },
})
export class CesCheckableMenuItemDirective implements OnInit {
  @Input('cesCheckableMenuItem') item!: CesCheckableMenuItem;
  @Input() parentModel: CesCheckableMenuItem[] = [];

  ngOnInit() {
    if (this.item.closeOnClick === undefined && this.item.checkable) {
      this.item.closeOnClick = false;
    }

    if (
      this.item.checkable &&
      this.item.exclusiveGroup &&
      this.item.alwaysSelected &&
      this.parentModel &&
      Array.isArray(this.parentModel)
    ) {
      const groupHasCheckedItem = this.hasCheckedItemInGroup(this.parentModel, this.item.exclusiveGroup);

      if (!groupHasCheckedItem) {
        this.checkFirstItemInGroup(this.parentModel, this.item.exclusiveGroup);
      }
    }
  }

  onClick(event: MouseEvent) {
    if (this.item.checkable && this.item.closeOnClick === false) {
      event.stopPropagation();
      event.preventDefault();

      if (this.item.checked && this.item.exclusiveGroup) {
        const groupAlwaysSelected = this.isGroupAlwaysSelected(this.item.exclusiveGroup);

        if (groupAlwaysSelected && this.isOnlyCheckedItemInGroup(this.item)) {
          return;
        }
      }

      this.item.checked = !this.item.checked;

      if (this.item.checked && this.item.exclusiveGroup && this.parentModel && this.parentModel.length > 0) {
        this.uncheckOtherExclusiveItems();
      }

      if (this.item.command) {
        this.item.command({
          originalEvent: event,
          item: this.item,
        });
      }
    } else {
      if (this.item.checked && this.item.exclusiveGroup) {
        const groupAlwaysSelected = this.isGroupAlwaysSelected(this.item.exclusiveGroup);

        if (groupAlwaysSelected && this.isOnlyCheckedItemInGroup(this.item)) {
          return;
        }
      }

      this.item.checked = !this.item.checked;

      if (this.item.checked && this.item.exclusiveGroup && this.parentModel && this.parentModel.length > 0) {
        this.uncheckOtherExclusiveItems();
      }
    }
  }

  private uncheckOtherExclusiveItems() {
    if (!this.item.exclusiveGroup) return;

    this.findAndUncheckExclusiveItems(this.parentModel);
  }

  private findAndUncheckExclusiveItems(items: CesCheckableMenuItem[]) {
    if (!items || !Array.isArray(items)) return;

    items.forEach(menuItem => {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        this.findAndUncheckExclusiveItems(menuItem.items);
      }

      if (menuItem.checkable && menuItem.exclusiveGroup === this.item.exclusiveGroup && menuItem !== this.item) {
        menuItem.checked = false;
      }
    });
  }

  private isGroupAlwaysSelected(groupName: string): boolean {
    if (!groupName || !this.parentModel || !Array.isArray(this.parentModel)) return false;

    const result = this.findGroupAlwaysSelectedFlag(this.parentModel, groupName);
    return result;
  }

  private findGroupAlwaysSelectedFlag(items: CesCheckableMenuItem[], groupName: string): boolean {
    if (!items || !Array.isArray(items)) return false;

    for (const menuItem of items) {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        const subResult = this.findGroupAlwaysSelectedFlag(menuItem.items, groupName);
        if (subResult) return true;
      }

      if (menuItem.checkable && menuItem.exclusiveGroup === groupName && menuItem.alwaysSelected) {
        return true;
      }
    }

    return false;
  }

  private isOnlyCheckedItemInGroup(item: CesCheckableMenuItem): boolean {
    if (!item.exclusiveGroup || !this.parentModel || !Array.isArray(this.parentModel)) return false;

    const checkedCount = this.countCheckedItemsInGroup(this.parentModel, item.exclusiveGroup);

    return checkedCount === 1;
  }

  private countCheckedItemsInGroup(items: CesCheckableMenuItem[], groupName: string): number {
    if (!items || !Array.isArray(items)) return 0;

    let count = 0;

    items.forEach(menuItem => {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        count += this.countCheckedItemsInGroup(menuItem.items, groupName);
      }

      if (menuItem.checkable && menuItem.exclusiveGroup === groupName && menuItem.checked) {
        count++;
      }
    });

    return count;
  }

  private hasCheckedItemInGroup(items: CesCheckableMenuItem[], groupName: string): boolean {
    if (!items || !Array.isArray(items)) return false;

    for (const menuItem of items) {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        const subResult = this.hasCheckedItemInGroup(menuItem.items, groupName);
        if (subResult) return true;
      }

      if (menuItem.checkable && menuItem.exclusiveGroup === groupName && menuItem.checked) {
        return true;
      }
    }

    return false;
  }

  private checkFirstItemInGroup(items: CesCheckableMenuItem[], groupName: string): boolean {
    if (!items || !Array.isArray(items)) return false;

    for (const menuItem of items) {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        const subResult = this.checkFirstItemInGroup(menuItem.items, groupName);
        if (subResult) return true;
      }

      if (menuItem.checkable && menuItem.exclusiveGroup === groupName) {
        menuItem.checked = true;
        return true;
      }
    }

    return false;
  }
}
